import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Popper,
  ClickAwayListener,
  Paper,
  Stack,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CustomAvatar from '../../../component/CustomAvatar';
import ChatIcon2 from '../../../svg/ChatIcon2';
import ProfileIcon2 from '../../../svg/ProfileIcon2';
import { followCreator } from '../../../utils/apis/memeverse';
import { toast } from 'react-toastify';
import { useAuth } from '../../../hooks/useAuth';
import { findAndReplaceLinks } from '../../../utils/helper';
import Markdown from '../../userChat/Markdown';

const UserProfileHoverDialog = ({ post_user_details, is_requester_following = false, show_following=true }) => {
  const navigate = useNavigate();
  const { authUser, setLoginModalVisible } = useAuth();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const chatUrl = `/${post_user_details.userName}/chat`;
  const profileUrl = `/${post_user_details.userName}`;

  const isTouch = useMediaQuery('(hover: none), (pointer: coarse)');

  const {
    fullName,
    profilePicUrl,
    shortBio,
    userName,
    user_id,
    is_creator = false,
  } = post_user_details || {};

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (isTouch) {
      setOpen((prev) => !prev);
    }
  };

  const handleFollow = (e) => {
    e.stopPropagation();
    if (!authUser) {
      return setLoginModalVisible({ isOpen: true, formType: 'signin', currentStep: 1 })
    } else {
      followCreator(authUser.uid, user_id)
        .then((res) => {
          if (res.ok) {
            return res.json();
          }
        })
        .then((data) => {
          // data has "action": "followed", "docId": "id", "success": "true"
          // just check existence of docId
          if (data.docId) {
            toast.success(
              `Now following ${post_user_details?.fullName || 'creator'}`
            );
          } else {
            toast.warning(
              `Unfollowed ${post_user_details?.fullName || 'creator'}`
            );
          }
        })
        .catch((err) => {
          console.log(err);
        })
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (open && isTouch) {
        handleClose();
      }
    };

    if (open && isTouch) {
      window.addEventListener('scroll', handleScroll, true);

      document.addEventListener('touchstart', handleTouchStart, true);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('touchstart', handleTouchStart, true);
    };
  }, [open, isTouch]);

  const handleTouchStart = (e) => {
    if (anchorRef.current && anchorRef.current.contains(e.target)) {
      return;
    }

    const popperElements = document.querySelectorAll('.MuiPaper-root');
    for (let i = 0; i < popperElements.length; i++) {
      if (popperElements[i].contains(e.target)) {
        return;
      }
    }

    handleClose();
  };

  return (
    <div
      ref={anchorRef}
      onClick={handleClick}
      onMouseEnter={!isTouch ? handleOpen : undefined}
      onMouseLeave={!isTouch ? handleClose : undefined}
      style={{
        position: 'relative',
        display: 'inline-block',
        width: 48,
        height: 48,
      }}
      className='user-profile-trigger'
    >
      <CustomAvatar
        src={profilePicUrl}
        sx={{ width: '100%', height: '100%' }}
      />

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement='right'
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, -10],
            },
          },
          {
            name: 'flip',
            enabled: true,
            options: {
              fallbackPlacements: ['bottom', 'right', 'left'],
              altBoundary: true,
              rootBoundary: 'viewport',
              padding: 8,
            },
          },
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              altAxis: true,
              altBoundary: true,
              tether: true,
              rootBoundary: 'viewport',
              padding: 8,
            },
          },
        ]}
        disablePortal={false}
        popperOptions={{
          strategy: 'absolute',
        }}
        sx={{ zIndex: 1400 }}
      >
        {({ TransitionProps }) => (
          <div {...TransitionProps}>
            <ClickAwayListener onClickAway={handleClose} touchEvent="onTouchEnd">
              <Paper
                elevation={4}
                sx={{
                  overflow: 'hidden',
                  width: 320,
                  zIndex: 1300,
                  maxHeight: '80vh',
                  overflowY: 'auto',
                }}
                className="user-profile-popper"
              >
                <Stack
                  sx={{
                    padding: 2,
                    backgroundColor: theme.palette.background.default,
                  }}
                >
                  {/* Avatar and Follow Button */}
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                    sx={{ pb: 1.5 }}
                  >
                    <CustomAvatar
                      src={profilePicUrl}
                      sx={{ width: 72, height: 72 }}
                    />

                    {show_following && (is_requester_following ? (
                      <Button
                        variant='contained'
                        color='secondary'
                        size='medium'
                        onClick={handleFollow}
                      >
                        Following
                      </Button>
                    ) : (
                      <Button
                        variant='contained'
                        color='primary'
                        size='medium'
                        startIcon={<AddIcon />}
                        onClick={handleFollow}
                      >
                        Follow
                      </Button>
                    ))}
                  </Stack>

                  {/* Full Name */}
                  <Typography
                    variant='h7'
                    fontWeight={600}
                    color='text.primary'
                  >
                    {fullName}
                  </Typography>

                  {/* Username */}
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ pb: 1 }}
                  >
                    @{userName}
                  </Typography>

                  {/* Bio */}
                  {shortBio && (
                    <Typography
                      component="div"
                      variant='body2'
                      color='text.primary'
                      sx={{ pb: 1 }}
                    >
                      <Markdown className={`markdown`}>
                        {findAndReplaceLinks(shortBio)}
                      </Markdown>
                    </Typography>
                  )}

                  {/* Action Buttons */}
                  {is_creator && <Stack
                    direction='row'
                    spacing={2}
                    justifyContent='flex-start'
                  >
                    <Button
                      variant='contained'
                      color='primary'
                      size='medium'
                      id={`button-voice-${userName}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(chatUrl, { state: { useVoiceChat: false } });
                      }}
                      sx={{ zIndex: 2, flex: 1 }}
                      startIcon={
                        <ChatIcon2 fill={theme.palette.secondary.main} />
                      }
                    >
                      Chat
                    </Button>

                    <Button
                      variant='contained'
                      color='secondary'
                      size='medium'
                      id={`button-profile-${userName}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(profileUrl);
                      }}
                      sx={{ zIndex: 2, flex: 1 }}
                      startIcon={
                        <ProfileIcon2 fill={theme.palette.primary.main} />
                      }
                    >
                      View Profile
                    </Button>
                  </Stack>}
                </Stack>
              </Paper>
            </ClickAwayListener>
          </div>
        )}
      </Popper>
    </div>
  );
};

export default UserProfileHoverDialog;
